import { createApi } from '@reduxjs/toolkit/query/react';
import { ENDPOINT_CONSTANTS } from '../../constant';
import baseQueryWithReauth from 'service/baseQuery/baseQuery';

type ApiResponse<T> = {
  data: T;
  status: number;
  message?: string;
};

export const authQuery = createApi({
  reducerPath: 'blogApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['AUTH_ME', 'USER_INVITE'],
  endpoints: builder => ({
    registerInstitution: builder.mutation({
      query: payload => ({
        url: ENDPOINT_CONSTANTS.INSTITUTION_REGISTER,
        method: 'POST',
        body: payload
      })
    }),
    login: builder.mutation({
      query: payload => ({
        url: ENDPOINT_CONSTANTS.LOGIN,
        method: 'POST',
        body: payload
      })
    }),
    userMe: builder.query({
      query: () => ({
        url: ENDPOINT_CONSTANTS.ME,
        method: 'GET'
      }),
      providesTags: ['AUTH_ME']
    }),
    updateCandidateProfile: builder.mutation({
      query: payload => ({
        url: ENDPOINT_CONSTANTS.CANDIDATE_PROFILE,
        method: 'PUT',
        body: payload
      }),
      invalidatesTags: ['AUTH_ME']
    }),
    updateInstitutionProfile: builder.mutation({
      query: payload => ({
        url: ENDPOINT_CONSTANTS.INSTITUTION_PROFILE,
        method: 'PUT',
        body: payload
      }),
      invalidatesTags: ['AUTH_ME']
    }),
    updateRecruiterProfile: builder.mutation({
      query: payload => ({
        url: ENDPOINT_CONSTANTS.RECRUITER_PROFILE,
        method: 'PUT',
        body: payload
      }),
      invalidatesTags: ['AUTH_ME']
    }),
    updateProfileImage: builder.mutation({
      query: payload => ({
        url: ENDPOINT_CONSTANTS.PROFILE_IMAGE,
        method: 'PUT',
        body: payload
      }),
      invalidatesTags: ['AUTH_ME']
    }),
    resentVerficationLink: builder.mutation({
      query: id => ({
        url: `${ENDPOINT_CONSTANTS.VERIFICATION_LINK}/${id}`,
        method: 'POST'
      })
    }),
    registerRecruiter: builder.mutation({
      query: payload => ({
        url: ENDPOINT_CONSTANTS.RECRUITER_REGISTER,
        method: 'POST',
        body: payload
      })
    }),
    resetPassword: builder.mutation({
      query: payload => ({
        url: ENDPOINT_CONSTANTS.RESET_PASSWORD,
        method: 'POST',
        body: payload
      })
    }),
    forgotPassword: builder.mutation({
      query: payload => ({
        url: ENDPOINT_CONSTANTS.FORGOT_PASSWORD_MAIL,
        method: 'POST',
        body: payload
      })
    }),
    inviteUser: builder.mutation({
      query: payload => ({
        url: ENDPOINT_CONSTANTS.INVITE_USER,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['USER_INVITE']
    }),
    findInvitedUser: builder.query({
      query: () => ({
        url: ENDPOINT_CONSTANTS.INVITE_USER,
        method: 'GET'
      }),
      transformResponse: <T>(
        response: ApiResponse<T> | undefined
      ): T | undefined => {
        return response?.data;
      },
      providesTags: ['USER_INVITE']
    }),
    updateUserInvite: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `${ENDPOINT_CONSTANTS.INVITE_USER}/${id}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: ['USER_INVITE']
    }),
    updateUserInviteStatus: builder.mutation({
      query: ({ id }) => ({
        url: `${ENDPOINT_CONSTANTS.INVITE_USER_STATUS}/${id}`,
        method: 'PATCH',
        body: {}
      }),
      invalidatesTags: ['USER_INVITE']
    }),
    deleteUserInvite: builder.mutation({
      query: ({ id }) => ({
        url: `${ENDPOINT_CONSTANTS.INVITE_USER}/${id}`,
        method: 'DELETE',
        body: {}
      }),
      invalidatesTags: ['USER_INVITE']
    }),
    resentUserInvite: builder.mutation({
      query: ({ id }) => ({
        url: `${ENDPOINT_CONSTANTS.RE_INVITE_USER}/${id}`,
        method: 'PUT',
        body: {}
      }),
      invalidatesTags: ['USER_INVITE']
    })
  })
});

export const {
  useRegisterInstitutionMutation,
  useResetPasswordMutation,
  useLoginMutation,
  useLazyUserMeQuery,
  useForgotPasswordMutation,
  useUpdateCandidateProfileMutation,
  useUpdateInstitutionProfileMutation,
  useUpdateProfileImageMutation,
  useResentVerficationLinkMutation,
  useRegisterRecruiterMutation,
  useUpdateRecruiterProfileMutation,
  useInviteUserMutation,
  useFindInvitedUserQuery,
  useUpdateUserInviteMutation,
  useUpdateUserInviteStatusMutation,
  useDeleteUserInviteMutation,
  useResentUserInviteMutation
} = authQuery;
